/**
 * Protocol is optional
 * @param url
 */
export const validateUrl = ({ url }: { url: string }) => {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // validate protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // validate domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // validate OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+,]*)*" + // validate port and path
      "(\\?[;&a-z\\d%_.~+=/,-]*)?" + // validate query string (includes slashes and dots for file paths in parameters)
      "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // validate fragment locator
  return urlPattern.test(url);
};

/**
 * Protocol is required
 * @param url
 */
export const validateXMLUrl = (url: string): boolean => {
  const xmlurlPattern = new RegExp("^(https?:\\/\\/).*\\.xml$", "i");
  return xmlurlPattern.test(url);
};

export const validateEmail = (email: string): boolean => {
  if (!email || email.trim() === "" || email.endsWith(".") || !email.includes("@")) {
    return false;
  }

  const trimmedEmail = email.trim();

  // Check for Polish diacritics (rejected by backend)
  const polishDiacriticsRegex = /[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/;
  if (polishDiacriticsRegex.test(trimmedEmail)) {
    return false;
  }

  // Main email pattern matching backend regex
  const emailPattern =
    /^[A-Za-zÀ-ÿ0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[A-Za-zÀ-ÿ0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[A-Za-zÀ-ÿ0-9](?:[A-Za-zÀ-ÿ0-9-]*[A-Za-zÀ-ÿ0-9])?\.)+[A-Za-zÀ-ÿ0-9](?:[A-Za-zÀ-ÿ0-9-]*[A-Za-zÀ-ÿ0-9])?$/i;

  if (!emailPattern.test(trimmedEmail)) {
    return false;
  }

  // Split email into user and domain parts
  const atIndex = trimmedEmail.lastIndexOf("@");
  const userPart = trimmedEmail.substring(0, atIndex);
  const domainPart = trimmedEmail.substring(atIndex + 1);

  // Validate user part length (max 64 characters as per backend)
  if (userPart.length > 64) {
    return false;
  }

  // Additional user part validation matching backend USER_PATTERN (simplified for JS compatibility)
  const userPattern =
    /^((\\.)|[^\s\x00-\x1F\x7F()<>@,;:'\\".\[\]]|')+(\.((\\.)|[^\s\x00-\x1F\x7F()<>@,;:'\\".\[\]]|')+)*$|^"(\\"|[^"])*"(\."(\\"|[^"])*")*$/;
  if (!userPattern.test(userPart)) {
    return false;
  }

  // Basic domain validation
  if (domainPart.startsWith(".") || domainPart.endsWith(".") || domainPart.includes("..")) {
    return false;
  }

  // Check for IP address pattern [x.x.x.x]
  const ipDomainMatch = domainPart.match(/^\[(.+)\]$/);
  if (ipDomainMatch) {
    // Basic IP validation (simplified version of backend logic)
    const ip = ipDomainMatch[1];
    const ipPattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipPattern.test(ip);
  }

  // Special domains whitelist (matching backend)
  const validDomains = ["multimoda.shop"];
  if (validDomains.includes(domainPart)) {
    return true;
  }

  // Basic domain format validation
  const domainPattern =
    /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  return domainPattern.test(domainPart);
};

/**
 * Validates phone number string value. It allows only digits, + (at the beginning of the string only), and spaces, with a maximum length of 20 characters.
 */
export const validatePhoneNumber = (value: string): boolean => {
  const isValidPhone =
    /^[\d+\s]{1,20}$/.test(value) &&
    value.length > 0 &&
    (value.match(/\+/g)?.length ?? 0) <= 1 &&
    (value.startsWith("+") || !value.includes("+"));
  return isValidPhone;
};

/**
 * Validates WhatsApp message template content
 */
const emojiRegex =
  /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu;

export const validateWhatsAppTemplate = (template: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  const openBraces = (template.match(/\{\{/g) || []).length;
  const closeBraces = (template.match(/\}\}/g) || []).length;
  if (openBraces !== closeBraces) {
    errors.push(window._trans._common.whatsappValidation.mismatchedBraces);
  }

  if (/^\{\{\d+\}\}/.test(template.trim()) || /\{\{\d+\}\}$/.test(template.trim())) {
    errors.push(window._trans._common.whatsappValidation.startsEndsWithVariable);
  }

  const variables = template.match(/\{\{(\d+)\}\}/g);
  if (variables) {
    const numbers = variables.map((v) => parseInt(v.replace(/[{}]/g, "")));
    const uniqueNumbers = [...new Set(numbers)].sort((a, b) => a - b);
    for (let i = 0; i < uniqueNumbers.length; i++) {
      if (uniqueNumbers[i] !== i + 1) {
        errors.push(window._trans._common.whatsappValidation.nonSequentialVariables);
        break;
      }
    }
  }

  if (/\{\{\d+\}\}\s+\{\{\d+\}\}/.test(template)) {
    errors.push(window._trans._common.whatsappValidation.adjacentVariables);
  }

  if (/\{\{\d*[#$%]\d*\}\}/.test(template)) {
    errors.push(window._trans._common.whatsappValidation.specialCharactersInVariables);
  }

  if (/\n{3,}/.test(template)) {
    errors.push(window._trans._common.whatsappValidation.tooManyNewlines);
  }

  const shortenedUrlPatterns = [/bit\.ly/i, /tinyurl/i, /t\.co/i, /goo\.gl/i, /ow\.ly/i, /short\.link/i];

  for (const pattern of shortenedUrlPatterns) {
    if (pattern.test(template)) {
      errors.push(window._trans._common.whatsappValidation.shortenedUrls);
      break;
    }
  }

  if (/https?:\/\/wa\.me\/\d+/i.test(template)) {
    errors.push(window._trans._common.whatsappValidation.whatsappDirectLinks);
  }

  const emojiCount = (template.match(emojiRegex) || []).length;
  if (emojiCount > 10) {
    errors.push(window._trans._common.whatsappValidation.tooManyEmojis);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates WhatsApp message template button text content
 */
export const validateWhatsAppTemplateButton = (text: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!text || text.trim().length === 0) {
    errors.push(window._trans._common.whatsappValidation.buttonEmpty);
  }

  emojiRegex.lastIndex = 0; // Reset regex state
  if (emojiRegex.test(text)) {
    errors.push(window._trans._common.whatsappValidation.buttonEmojis);
  }

  if (/[\*_~{}`\n\r]/.test(text)) {
    errors.push(window._trans._common.whatsappValidation.buttonFormatting);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates WhatsApp message template header content
 */
export const validateWhatsAppTemplateHeader = (header: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!header || header.trim().length === 0) {
    errors.push(window._trans._common.whatsappValidation.headerEmpty);
  }

  emojiRegex.lastIndex = 0; // Reset regex state
  if (emojiRegex.test(header)) {
    errors.push(window._trans._common.whatsappValidation.headerEmojis);
  }

  if (/[\*_~`\n\r]/.test(header)) {
    errors.push(window._trans._common.whatsappValidation.headerFormatting);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateCouponCode = (code: string): boolean => /^[A-Za-z0-9]{1,15}$/.test(code);
